import { Controller } from 'stimulus'
import { loadScript } from '@paypal/paypal-js'

const paypalConfig = {
  'client-id': window.env.PAYPAL_CLIENT_ID,
  currency: 'EUR',
  locale: 'de_DE',
  'disable-funding': [
    'card',
    'credit',
    'paylater',
    'bancontact',
    'blik',
    'eps',
    'ideal',
    'mercadopago',
    'mybank',
    'p24',
    'sepa',
    'sofort',
    'venmo'
  ]
}

export default class extends Controller {
  static targets = ['firstName', 'zipCode', 'paypalButtonsContainer', 'form', 'submitButton', 'errors',
    'taxIdInput', 'paymentProvider', 'paypalPui', 'paypalClientMetadataId', 'paypalDob']

  static values = {
    paypalCreateUrl: String,
    paypalWebhookUrl: String
  }

  csrfToken
  purchaseToken

  connect () {
    this.csrfToken = document.querySelector('[name=\'csrf-token\']')?.content
    if (this.paypalAllowed) { this.initializePaypal() }
    const paypalPuiModal = document.getElementById('paypalInfoModal')
    if (paypalPuiModal) {
      this.disablePaypalPuiFormFields()
      this.handleEnableDisableOfPuiFields()
    }
  }

  disconnect () {
    if (this.paypalAllowed) { this.destroyPaypal() }
  }

  toggleTaxIdField (event) {
    const taxField = this.taxIdInputTarget.closest('.tax-field')
    const companyName = event.target.value.trim()

    if (companyName.length > 0) {
      taxField.classList.add('d-flex')
      taxField.classList.remove('d-none')
      this.taxIdInputTarget.required = true
    } else {
      taxField.classList.add('d-none')
      taxField.classList.remove('d-flex')
      this.taxIdInputTarget.required = false
      this.taxIdInputTarget.value = ''
    }
  }

  get paypalAllowed () {
    return this.hasPaypalButtonsContainerTarget
  }

  submit () {
    const itemsAttributes = JSON.parse(this.data.get('datalayer-ecommerce-items'))

    dataLayer.push({
      event: 'ce_checkout',
      ecommerce: {
        checkout: {
          actionField: { step: 1, option: 'Stripe' },
          products: itemsAttributes
        }
      }
    })

    const variables = JSON.parse(this.data.get('datalayer-buyer-to-payment-click'))
    dataLayer.push({ ecommerce: null })
    dataLayer.push({
      ...variables,
      firstName: this.firstNameTarget.value,
      zipCode: this.zipCodeTarget.value,
      event: 'buyer-to-payment-click',
      ecommerce: {
        value: variables.value,
        currency: 'EUR'
      }
    })
  }

  initializePaypal () {
    loadScript(paypalConfig).then(this.renderPaypalButton.bind(this))

    const submitButtonWidth = this.submitButtonTarget.clientWidth
    this.paypalButtonsContainerTarget.style.width = `${submitButtonWidth}px`
  }

  handleEnableDisableOfPuiFields () {
    const paypalModal = document.getElementById('paypalInfoModal')

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          const target = mutation.target

          // Check common ways modals are shown/hidden
          const isVisible = target.style.display !== 'none'
          if (isVisible) {
            this.enablePaypalPuiFormFields()
          } else {
            this.disablePaypalPuiFormFields()
          }
        }
      })
    })

    // Start observing
    observer.observe(paypalModal, {
      attributes: true,
      attributeFilter: ['style', 'class']
    })
  }

  disablePaypalPuiFormFields () {
    this.paymentProviderTarget.value = 'stripe'
    this.paypalPuiTarget.disabled = true
    this.paypalClientMetadataIdTarget.disabled = true
    this.paypalDobTarget.disabled = true
  }

  enablePaypalPuiFormFields () {
    this.paymentProviderTarget.value = 'paypal'
    this.paypalPuiTarget.disabled = false
    this.paypalClientMetadataIdTarget.disabled = false
    this.paypalDobTarget.disabled = false
  }

  destroyPaypal () {
    this.paypalButtonsContainerTarget.replaceChildren()
  }

  renderPaypalButton () {
    paypal.Buttons({
      locale: 'de_DE',
      style: {
        layout: 'vertical',
        color: 'gold',
        shape: 'pill',
        size: 'responsive'
      },
      createOrder: this.paypalCreateOrder.bind(this),
      onApprove: this.paypalOnApprove.bind(this)
    }).render(this.paypalButtonsContainerTarget)
  }

  paypalCreateOrder (data, actions) {
    const paymentAttributes = JSON.parse(this.element.getAttribute('data-purchase-datalayer-paypal-info-attributes'))
    const shippingAttributes = JSON.parse(this.element.getAttribute('data-purchase-datalayer-shipping-info-attributes'))

    dataLayer.push(paymentAttributes)
    dataLayer.push(shippingAttributes)

    const formData = new FormData(this.formTarget)
    formData.set('purchase[payment_provider]', 'paypal')
    formData.set('purchase[paypal_service_type]', 'paypal')

    return fetch(this.paypalCreateUrlValue, {
      method: 'post',
      headers: {
        'X-CSRF-Token': this.csrfToken
      },
      body: formData
    }).then(
      response => response.json()
    ).then(data => {
      if (data.token) {
        this.purchaseToken = data.token
        return data.order_id
      } else {
        const errorContainer = this.errorsTarget
        errorContainer.innerHTML = data.errors
        errorContainer.classList.remove('d-none')
        errorContainer.scrollIntoView()
      }
    })
  }

  paypalOnApprove (data, actions) {
    return fetch(this.paypalWebhookUrlValue, {
      method: 'post',
      headers: {
        'X-CSRF-Token': this.csrfToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        token: this.purchaseToken,
        payload: data
      })
    }).then(function (response) {
      return response.json()
    }).then(function (data) {
      window.location.href = data.success_url
    })
  }
}
